<script setup lang="ts">
  import {
    computed,
    getCurrentInstance,
    onBeforeUnmount,
    ref,
    watch,
  } from 'vue';
  import PosUiSheet from '@/components/pos-v2/ui/PosUiSheet.vue';
  import type { TSheetHeight } from '@/components/pos-v2/ui/pos-ui-sheet.utils';
  import { useCatalogPage } from '@/composition/catalog/catalog-page-context';
  import { useUi } from '@/composition/use-ui';
  import { AlertType } from '@/interfaces/IAlert';
  import V2CatalogCategoryEditor from './category/V2CatalogCategoryEditor.vue';
  import V2CatalogProductEditor from './product/V2CatalogProductEditor.vue';

  /**
   * The editor host (plan §5.12): one `PosUiSheet` in the canvas aside,
   * docked beside the list on wide screens and a bottom sheet otherwise,
   * never remounted on a tier change. It closes through the dirty guard,
   * and checks deep links once the catalog is loaded.
   */
  /** The phone keyboard covers lower fields: scroll after it opens. */
  const FOCUS_SCROLL_DELAY_MS = 250;
  /**
   * Tablets centre the sheet at this width, and so do desktops while the
   * docked «Asistente» panel turns the editor into a sheet (plan §9.3).
   */
  const TABLET_SHEET_WIDTH = 680;

  const { state, data, layout, url, confirmDiscard } = useCatalogPage();
  const ui = useUi();
  const proxy = getCurrentInstance()!.proxy;
  const t = (key: string) => String(proxy.$t(key));

  const target = computed(() => state.editor);
  const docked = computed(() => layout.columns.value.asideDocked);
  const tier = computed(() => layout.tier.value);
  const height = ref<TSheetHeight>('lg');

  /**
   * One editor per target: switching products starts a fresh one, and so
   * does another «nuevo producto» (other category or name to start from).
   */
  const editorKey = computed(() => {
    const current = target.value;
    if (!current) return '';
    const key = `${current.kind}:${current.id}`;
    return current.id === 'new'
      ? `${key}:${current.categoryId ?? ''}:${current.prefillName}`
      : key;
  });

  const sheetTitle = computed(() => {
    if (state.editorName) return state.editorName;
    if (target.value?.kind === 'category')
      return t('menuPage.editor.newCategory');
    return t('menuPage.editor.newProduct');
  });

  //#region Closing

  let closing = false;
  async function requestClose() {
    if (closing || !state.editor) return;
    closing = true;
    try {
      if (await confirmDiscard()) url.closeEditor();
    } finally {
      closing = false;
    }
  }

  function onSheetInput(open: boolean) {
    if (!open) void requestClose();
  }

  /**
   * Esc inside a menu only closes the menu. Otherwise the sheet handles it,
   * and docked (the sheet ignores it) the editor closes here.
   */
  function onKeydown(event: KeyboardEvent) {
    if (event.key !== 'Escape') return;
    const origin = event.target;
    if (origin instanceof Element && origin.closest('.pos-ui-popover')) {
      event.stopPropagation();
      return;
    }
    if (!docked.value) return;
    event.stopPropagation();
    void requestClose();
  }

  //#endregion

  //#region Phone keyboard

  let focusTimer: number | null = null;
  function onFocusIn(event: FocusEvent) {
    if (tier.value !== 'phone') return;
    const field = event.target;
    if (
      !(field instanceof HTMLInputElement) &&
      !(field instanceof HTMLTextAreaElement)
    )
      return;
    if (focusTimer !== null) window.clearTimeout(focusTimer);
    focusTimer = window.setTimeout(() => {
      focusTimer = null;
      if (field.isConnected) field.scrollIntoView({ block: 'nearest' });
    }, FOCUS_SCROLL_DELAY_MS);
  }

  onBeforeUnmount(() => {
    if (focusTimer !== null) window.clearTimeout(focusTimer);
  });

  //#endregion

  //#region Deep links

  /** The editor already checked (a deletion later shows the editor's notice). */
  let checkedKey = '';

  /**
   * When an editor opens and the catalog is loaded: a product or category
   * that does not exist closes with a notice; one of another menu switches
   * to that menu; a new product with no category to go in becomes a new
   * category.
   */
  watch(
    () =>
      [
        target.value,
        data.loadStatus.value,
        data.menusLoaded.value,
        data.sections.value,
      ] as const,
    ([editor, status, menusLoaded]) => {
      if (!editor) {
        checkedKey = '';
        return;
      }
      if (status !== 'ready' || !menusLoaded || checkedKey === editorKey.value)
        return;
      checkedKey = editorKey.value;
      if (editor.kind === 'item') {
        if (editor.id === 'new') {
          if (!data.sections.value.length) url.replaceTarget('category', 'new');
          return;
        }
        const item = data.itemById.value[editor.id];
        const category = item ? data.categoryOf(item) : null;
        if (!item || !category) {
          ui.showToast(AlertType.Info, t('menuPage.deeplink.itemGone'));
          state.editorDirty = false;
          url.closeEditor();
        } else if ((category.menuId ?? null) !== state.menuId)
          url.showMenuOf(category.menuId ?? null);
        return;
      }
      if (editor.id === 'new') return;
      const category = data.categoryById.value.get(editor.id);
      if (!category) {
        ui.showToast(AlertType.Info, t('menuPage.deeplink.categoryGone'));
        state.editorDirty = false;
        url.closeEditor();
      } else if ((category.menuId ?? null) !== state.menuId)
        url.showMenuOf(category.menuId ?? null);
    },
    { immediate: true }
  );

  //#endregion
</script>

<template>
  <PosUiSheet
    class="v2-catalog-editor-host"
    :class="{ 'is-docked': docked }"
    :value="!!target"
    :docked="docked"
    :title="sheetTitle"
    :height.sync="height"
    :max-width="tier === 'phone' ? undefined : TABLET_SHEET_WIDTH"
    :focus-key="editorKey"
    @input="onSheetInput"
  >
    <div
      v-if="target"
      class="host-frame"
      @keydown="onKeydown"
      @focusin="onFocusIn"
    >
      <V2CatalogProductEditor
        v-if="target.kind === 'item'"
        :key="editorKey"
        :target="target"
        :docked="docked"
        @close="requestClose"
      />
      <V2CatalogCategoryEditor
        v-else
        :key="editorKey"
        :target="target"
        :docked="docked"
        @close="requestClose"
      />
    </div>
  </PosUiSheet>
</template>

<style scoped lang="scss">
  .v2-catalog-editor-host {
    .host-frame {
      @apply tw-flex tw-min-h-0 tw-flex-1 tw-flex-col;
    }

    &.is-docked {
      @apply tw-h-full;

      .host-frame {
        @apply tw-overflow-hidden tw-rounded-xl tw-border tw-border-solid tw-border-neutral-200;
      }
    }
  }
</style>
